import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Grid,
  Card,
  Image,
  Button,
  Loader,
  Message,
  Label,
  Icon,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import * as privateApi from "../../api/privateApi";

const GrigliaMaestri = ({
  isOpen,
  onClose,
  onSelectMaestro,
  idScuola,
  codScuola,
}) => {
  const { t } = useTranslation();
  const [maestri, setMaestri] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMaestri = useCallback(async () => {
    if (!idScuola) {
      setError(t("id_scuola_non_disponibile"));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await privateApi.getMaestri(idScuola);
      console.log("Fetched maestri:", response);
      setMaestri(response || []);
    } catch (err) {
      console.error("Error fetching maestri:", err);
      setError(t("errore_caricamento_maestri"));
    } finally {
      setLoading(false);
    }
  }, [idScuola, t]);

  useEffect(() => {
    console.log(
      "GrigliaMaestri useEffect - isOpen:",
      isOpen,
      "idScuola:",
      idScuola
    );
    if (isOpen && idScuola) {
      fetchMaestri();
    }
  }, [isOpen, idScuola, fetchMaestri]);

  const handleSelectMaestro = (maestro) => {
    onSelectMaestro(
      maestro.idSkiwork,
      maestro.titoloWeb || `Maestro ${maestro.idSkiwork}`,
      maestro
    );
    onClose();
  };

  const renderLanguages = (lingueDesc) => {
    if (!lingueDesc || lingueDesc.length === 0) return null;

    const filteredLingue = lingueDesc.filter(
      (lingua) => lingua && lingua.length > 0
    );
    if (filteredLingue.length === 0) return null;

    return filteredLingue.map(
      (lingua, index) =>
        lingua.length > 0 && (
          <Label key={index} size="mini" color="blue">
            {lingua}
          </Label>
        )
    );
  };

  const renderSpecialities = (specialitaDesc) => {
    if (!specialitaDesc || specialitaDesc.length === 0) return null;

    const filteredSpecialita = specialitaDesc.filter(
      (specialita) => specialita && specialita.length > 0
    );
    if (filteredSpecialita.length === 0) return null;

    return filteredSpecialita.map((specialita, index) => (
      <Label key={index} size="mini" color="green">
        {specialita}
      </Label>
    ));
  };

  const getGenderIcon = (sesso) => {
    if (sesso === "M") return "male";
    if (sesso === "F") return "female";
    return "user";
  };

  const getGenderColor = (sesso) => {
    if (sesso === "M") return "blue";
    if (sesso === "F") return "pink";
    return "grey";
  };

  return (
    <Modal open={isOpen} onClose={onClose} size="fullscreen" closeIcon>
      <Modal.Header>
        <Icon name="graduation cap" />
        {t("scegli_tuo_maestro")}
      </Modal.Header>
      <Modal.Content scrolling>
        {loading && (
          <div style={{ textAlign: "center", padding: "2em" }}>
            <Loader active inline="centered" />
            <p>{t("caricamento_maestri")}</p>
          </div>
        )}

        {error && (
          <Message negative>
            <Message.Header>{t("errore")}</Message.Header>
            <p>{error}</p>
          </Message>
        )}

        {!loading && !error && maestri.length === 0 && (
          <Message info>
            <Message.Header>{t("nessun_maestro_disponibile")}</Message.Header>
            <p>{t("nessun_maestro_disponibile_desc")}</p>
          </Message>
        )}

        {!loading && !error && maestri.length > 0 && (
          <Grid stackable>
            {maestri.map((maestro, idx) => (
              <Grid.Column
                key={maestro.idSkiwork || maestro.IdSkiwork || idx}
                mobile={16}
                tablet={8}
                computer={3}
                style={{ marginBottom: "1em", display: "flex" }}
              >
                <Card
                  fluid
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                  }}
                >
                  <div
                    className="skiwork-image-maestri-container"
                    style={{
                      width: "100%",
                      height: 0,
                      paddingBottom: "120%",
                      position: "relative",
                      overflow: "hidden",
                      backgroundColor: "#f4f4f4",
                    }}
                  >
                    <img
                      src={
                        maestro.picture
                          ? `https://skiwork.blob.core.windows.net/webimg/${codScuola.toUpperCase()}/Maestri/${
                              maestro.picture
                            }`
                          : maestro.sesso === "F"
                          ? require("../../assets/images/people/AvatarMaestra.jpg")
                          : require("../../assets/images/people/AvatarMaestro.jpg")
                      }
                      alt={maestro.titoloWeb || maestro.sesso || "Maestro"}
                      className="skiwork-image"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center 20%",
                      }}
                      onError={(e) => {
                        e.target.src =
                          maestro.sesso === "F"
                            ? require("../../assets/images/people/AvatarMaestra.jpg")
                            : require("../../assets/images/people/AvatarMaestro.jpg");
                      }}
                    />
                  </div>
                  <Card.Content style={{ flexGrow: 1 }}>
                    <Card.Header>
                      <Icon
                        name={getGenderIcon(maestro.sesso)}
                        color={getGenderColor(maestro.sesso)}
                      />
                      {maestro.titoloWeb || `Maestro ${maestro.idSkiwork}`}
                    </Card.Header>
                    {maestro.descWeb && (
                      <Card.Description>{maestro.descWeb}</Card.Description>
                    )}
                  </Card.Content>

                  {(maestro.lingueDesc?.length > 0 ||
                    maestro.specialitaDesc?.length > 0) && (
                    <Card.Content extra>
                      {maestro.lingueDesc?.length > 0 && (
                        <div style={{ marginBottom: "0.5em" }}>
                          <strong>{t("lingue")}:</strong>
                          <div style={{ marginTop: "0.25em" }}>
                            {renderLanguages(maestro.lingueDesc)}
                          </div>
                        </div>
                      )}

                      {maestro.specialitaDesc?.length > 0 && (
                        <div>
                          <strong>{t("specialita")}:</strong>
                          <div style={{ marginTop: "0.25em" }}>
                            {renderSpecialities(maestro.specialitaDesc)}
                          </div>
                        </div>
                      )}
                    </Card.Content>
                  )}

                  <Card.Content extra>
                    <Button
                      primary
                      fluid
                      onClick={() => handleSelectMaestro(maestro)}
                      icon="check"
                      content={t("scegli").toUpperCase()}
                    />
                  </Card.Content>
                </Card>
              </Grid.Column>
            ))}
          </Grid>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} icon="cancel" content={t("annulla")} />
      </Modal.Actions>
    </Modal>
  );
};

export default GrigliaMaestri;
